<template>
  <div class="categories-page">

    <!-- Page header -->
    <div class="page-header">
      <button class="back-btn" onclick="window.history.back()"><i class="ft-arrow-left"></i></button>
      <div class="header-info">
        <span class="page-title">{{ periodName || 'Menu' }}</span>
        <span class="page-subtitle" v-if="periodName">
          <i class="ft-clock"></i> Select a category
        </span>
      </div>
      <router-link to="/checkout" class="cart-btn" v-if="cart.length > 0">
        <i class="ft-shopping-cart"></i>
        <span class="cart-badge">{{ cart.length }}</span>
      </router-link>
    </div>

    <!-- ── Inline add-to-cart confirmation ── -->
    <transition name="toast-slide">
      <div class="cart-toast" v-if="toastMessage">
        <div class="cart-toast-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div class="cart-toast-body">
          <span class="cart-toast-title">Added to order</span>
          <span class="cart-toast-item">{{ toastMessage }}</span>
        </div>
        <button class="cart-toast-close" @click="toastMessage = ''">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    </transition>

    <!-- Cart summary -->
    <div class="section-pad" v-if="cart.length > 0">
      <Cart show-delete="true" show-quantity="false" show-notes="false" show-checkout="true" @item-removed="onCartItemRemoved">
        <template v-slot:extra-action v-if="free_issue != 'Y'">
          <router-link :to="generateLink()" class="action-btn action-btn--order-again">
            <i class="ft-plus-circle"></i> Place Another
          </router-link>
        </template>
      </Cart>
    </div>

    <!-- Loading -->
    <div class="loading-state" v-if="loading">
      <div class="spinner"></div>
      <span>Loading menu…</span>
    </div>

    <!-- Empty / error -->
    <div class="empty-state" v-if="!loading && categories.length === 0">
      <div class="empty-icon-wrap">
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>
      <p class="empty-title">No categories found</p>
      <p class="empty-sub">No menu categories are available for this period.</p>
      <button class="empty-back-btn" onclick="window.history.back()"><i class="ft-arrow-left"></i> Go Back</button>
    </div>

    <!-- ── Category scroll strip ── -->
    <div class="section-pad cat-section" v-if="!loading && categories.length > 0">
      <div class="cat-scroll-wrap">

        <button class="scroll-arrow" @click="scrollCats(-1)" v-show="canScrollLeft" aria-label="Scroll left">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>

        <div class="cat-strip" ref="catStrip" @scroll="updateArrows">
          <!-- Standard mode -->
          <button
            v-if="free_issue != 'Y'"
            class="cat-chip"
            :class="{ 'cat-chip--active': selectedCategoryId == category.id }"
            v-for="(category, index) in categories"
            :key="category.id"
            @click="selectCategory(category.id)"
          >
            <span class="cat-chip-icon" v-html="categoryIconSvg(category.name)"></span>
            {{ category.name }}
            <span class="cat-chip-added" v-if="categoryCartItem(category) || categoryAlreadyOrdered(category)">✔</span>
          </button>

          <!-- Free issue mode -->
          <button
            v-if="free_issue == 'Y'"
            class="cat-chip"
            :class="{ 'cat-chip--active': selectedCategoryId == category.category_id }"
            v-for="(category, index) in categories"
            :key="category.category_id"
            @click="selectCategoryFreeIssue(category)"
          >
            <span class="cat-chip-icon" v-html="categoryIconSvg(category.category)"></span>
            {{ category.category }}
          </button>
        </div>

        <button class="scroll-arrow" @click="scrollCats(1)" v-show="canScrollRight" aria-label="Scroll right">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>

      </div>
    </div>

    <!-- ── Items for selected category ── -->
    <div class="section-pad items-section" v-if="!loading && selectedCategoryId && filteredItems.length > 0">
      <div class="product-grid">
        <div
          class="product-card"
          :class="{ 'product-card--ordered': itemAlreadyOrdered(item.id || item.meal_id) }"
          v-for="item in filteredItems"
          :key="item.id || item.meal_id"
          @click="viewItem(item)"
        >
          <div class="cart-count" v-if="cartItemCount(item.id || item.meal_id)">
            {{ cartItemCount(item.id || item.meal_id) }}
          </div>
          <div class="product-icon-wrap" v-html="itemIconSvg(item)"></div>
          <span class="product-name">{{ item.label }}</span>
          <p class="product-desc" v-if="item.description">{{ item.description }}</p>
          <div class="select-btn" :class="{ 'select-btn--ordered': itemAlreadyOrdered(item.id || item.meal_id) }">
            <template v-if="itemAlreadyOrdered(item.id || item.meal_id)">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Selected
            </template>
            <template v-else>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Select
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty category items state -->
    <div class="empty-items" v-if="!loading && selectedCategoryId && filteredItems.length === 0 && !loadingItems">
      <i class="ft-inbox"></i>
      <p>No items in this category.</p>
    </div>

    <!-- Items loading -->
    <div class="loading-state" style="padding-top:24px" v-if="loadingItems">
      <div class="spinner"></div>
      <span>Loading items…</span>
    </div>


  </div>
</template>

<script>
import swal from 'sweetalert'
import Cart from '@/components/navigation/Cart.vue'

const DOT_COLORS = [
  '#1a587b','#0ea5e9','#10b981','#f59e0b',
  '#8b5cf6','#ef4444','#ec4899','#14b8a6'
]

export default {
  name: 'categories',
  components: { Cart },
  data: function () {
    var period = null
    try { period = JSON.parse(localStorage.getItem('period')) } catch (e) {}
    return {
      categories: [],
      allItems: [],       // all items from formatted_menu (standard mode)
      loading: true,
      loadingItems: false,
      free_issue: window.localStorage.getItem('free_issue'),
      cart: window.localStorage.getItem('cart') ? JSON.parse(window.localStorage.getItem('cart')) : [],
      periodName: period ? period.title || period.name || '' : '',
      selectedCategoryId: null,
      freeIssueItems: [],
      canScrollLeft: false,
      canScrollRight: false,
      toastMessage: '',
      existingOrderMenuIds: [] // menu ids already on an order for this bed on this date & period
    }
  },
  computed: {
    filteredItems: function () {
      if (this.free_issue === 'Y') return this.freeIssueItems
      if (!this.selectedCategoryId) return []
      var id = this.selectedCategoryId
      return this.allItems.filter(function (item) {
        return item.meal_category_id == id
      })
    }
  },
  mounted: function () {
    this.$store.commit('SET_LAYOUT', 'AppLayout')
    var that = this
    var user = JSON.parse(window.localStorage.getItem('user'))
    var merchantId = user.merchant.id
    var date = window.localStorage.getItem('date')
    var typeId = localStorage.getItem('menu_type_id')

    // Restore previously selected category (after returning from Product.vue)
    var storedCat = localStorage.getItem('selected_category_id')
    if (storedCat) that.selectedCategoryId = storedCat

    // Show inline confirmation if returning from an add-to-cart
    var addedItem = localStorage.getItem('cart_added_toast')
    if (addedItem) {
      localStorage.removeItem('cart_added_toast')
      that.toastMessage = addedItem
      setTimeout(function () { that.toastMessage = '' }, 3000)
    }

    try { var period = JSON.parse(localStorage.getItem('period')) } catch (e) { var period = null }

    // Pre-tick categories/items already on an existing order for this bed on this date & period
    if (that.free_issue !== 'Y' && period && period.id) {
      that.loadExistingOrderItems(merchantId, period)
    }

    // if (!typeId) {
    //   that.loading = false
    //   swal({ text: 'No menu type found for this patient. Please go back and re-select the patient.', icon: 'info', button: 'Ok' })
    //     .then(function () { that.$router.go(-1) })
    //   return
    // }

    // ── Free issue mode ──
    if (that.free_issue === 'Y') {
      var ward = JSON.parse(window.localStorage.getItem('ward'))
      var url = that.$config.apiUrl + 'canteenapp_merchant/free_issues_menu?type_id=' + typeId + '&merchant_id=' + merchantId + '&period=1&date=' + date + '&ward_id=' + ward.ward_id
      $.ajax({
        beforeSend: function (xhr) { xhr.setRequestHeader('Authorization', window.localStorage.getItem('token')) },
        url: url, type: 'get',
        success: function (data) {
          that.loading = false
          that.categories = data.data || []
          that.$nextTick(that.updateArrows)
          // Restore valid stored category, otherwise default to first
          if (that.categories.length > 0) {
            var match = that.selectedCategoryId &&
              that.categories.find(function (c) { return c.category_id == that.selectedCategoryId })
            that.selectCategoryFreeIssue(match || that.categories[0])
          }
        },
        error: function () {
          that.loading = false
          swal({ text: 'Unable to load menu. Please try again.', icon: 'info', button: 'Ok' }).then(function () { that.$router.go(-1) })
        }
      })
      return
    }

    // ── Standard mode: load categories and all items in parallel ──
    if (!period || !period.id) {
      that.loading = false
      swal({ text: 'No meal period selected. Please go back and select a period.', icon: 'info', button: 'Ok' })
        .then(function () { that.$router.go(-1) })
      return
    }

    var catUrl   = that.$config.apiUrl + 'canteenapp_merchant/meal_categories?merchant_id=' + merchantId + '&period=' + period.id + '&date=' + date + '&type_id=' + typeId
    var menuUrl  = that.$config.apiUrl + 'canteenapp_merchant/formatted_menu?type_id=' + typeId + '&merchant_id=' + merchantId + '&period=' + period.id + '&date=' + date

    var catDone = false, menuDone = false
    function checkDone () {
      if (catDone && menuDone) {
        that.loading = false
        that.$nextTick(that.updateArrows)
        if (that.categories.length > 0) {
          // Validate any stored category against the current menu
          var validStored = that.selectedCategoryId &&
            that.categories.some(function (c) { return c.id == that.selectedCategoryId })
          if (!validStored) {
            that.selectCategory(that.categories[0].id)
          }
        }
      }
    }

    $.ajax({
      beforeSend: function (xhr) { xhr.setRequestHeader('Authorization', window.localStorage.getItem('token')) },
      url: catUrl, type: 'get',
      success: function (data) {
        that.categories = data.data || []
        catDone = true; checkDone()
      },
      error: function () { catDone = true; checkDone() }
    })

    $.ajax({
      beforeSend: function (xhr) { xhr.setRequestHeader('Authorization', window.localStorage.getItem('token')) },
      url: menuUrl, type: 'get',
      success: function (data) {
        window.localStorage.setItem('inclusions', JSON.stringify(data.data.global_additions))
        window.localStorage.setItem('exclusions', JSON.stringify(data.data.global_exclusions))
        var items = []
        if (data.data.menu_items) {
          data.data.menu_items.forEach(function (cat) {
            cat.menu_items.forEach(function (meal) {
              meal.meal_category_id = cat.meal_category_id
              items.push(meal)
            })
          })
        }
        that.allItems = items
        menuDone = true; checkDone()
      },
      error: function () { menuDone = true; checkDone() }
    })
  },
  updated: function () {
    this.$nextTick(this.updateArrows)
  },
  methods: {
    // ── Category strip scroll ──────────────────────────────
    scrollCats: function (dir) {
      var el = this.$refs.catStrip
      if (!el) return
      el.scrollBy({ left: dir * 200, behavior: 'smooth' })
    },
    updateArrows: function () {
      var el = this.$refs.catStrip
      if (!el) return
      this.canScrollLeft  = el.scrollLeft > 4
      this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 4
    },

    // ── Existing order lookup (pre-tick already-ordered items) ──
    loadExistingOrderItems: function (merchantId, period) {
      var that = this
      var bed = JSON.parse(window.localStorage.getItem('bed'))
      if (!bed || !bed.bed_id) return
      var routeDate = this.$route.params.date
      var url = that.$config.apiUrl + 'medicater/calendar/?merchant_id=' + merchantId + '&bed_id=' + bed.bed_id
      $.ajax({
        beforeSend: function (xhr) { xhr.setRequestHeader('Authorization', window.localStorage.getItem('token')) },
        url: url, type: 'get',
        success: function (data) {
          var menuIds = []
          if (data.success === true && data.data && data.data.calendar) {
            data.data.calendar.forEach(function (day) {
              if (!day.orders || !day.orders[period.id]) return
              for (var key in day.orders[period.id]) {
                var order = day.orders[period.id][key]
                if (order.date === routeDate && order.period_id == period.id) {
                  (order.items || []).forEach(function (item) {
                    menuIds.push(item.menu_id)
                  })
                }
              }
            })
          }
          that.existingOrderMenuIds = menuIds
        }
      })
    },
    itemAlreadyOrdered: function (itemId) {
      return this.existingOrderMenuIds.some(function (id) { return id == itemId })
    },
    categoryAlreadyOrdered: function (category) {
      var that = this
      return this.allItems.some(function (item) {
        return item.meal_category_id == category.id && that.itemAlreadyOrdered(item.id)
      })
    },

    // ── Keep header/badges in sync when Cart removes an item ──
    onCartItemRemoved: function (newCart) {
      this.cart = newCart
    },

    // ── Category selection ─────────────────────────────────
    selectCategory: function (categoryId) {
      this.selectedCategoryId = categoryId
      localStorage.setItem('selected_category_id', categoryId)
    },
    selectCategoryFreeIssue: function (category) {
      this.selectedCategoryId = category.category_id
      this.freeIssueItems = category.menus || []
      localStorage.setItem('selected_category_id', category.category_id)
      localStorage.setItem('free_menu', JSON.stringify(category))
    },

    // ── Navigate to product detail ─────────────────────────
    viewItem: function (item) {
      window.localStorage.setItem('meal', JSON.stringify(item))
      var period = JSON.parse(localStorage.getItem('period'))
      var catId  = this.selectedCategoryId
      this.$router.push(
        '/preorder/' + this.$route.params.ward +
        '/' + this.$route.params.bed +
        '/' + this.$route.params.date +
        '/' + period.id +
        '/' + catId +
        '/products/' + item.id
      )
    },

    // ── Helpers ───────────────────────────────────────────
    dotColor: function (index) {
      return DOT_COLORS[index % DOT_COLORS.length]
    },
    categoryCartItem: function (category) {
      var period = JSON.parse(localStorage.getItem('period'))
      var cart = localStorage.getItem('cart') ? JSON.parse(localStorage.getItem('cart')) : []
      return cart.some(function (item) {
        return category.id === item.category_id && period.id == item.period_id
      })
    },
    cartItemCount: function (menuId) {
      var cart = JSON.parse(window.localStorage.getItem('cart'))
      if (!cart) return false
      var periodId = null
      try { periodId = JSON.parse(window.localStorage.getItem('period')).id } catch (e) {}
      var count = 0
      cart.forEach(function (c) {
        if (c.mealId == menuId && (!periodId || c.period_id == periodId)) count += Number(c.quantity)
      })
      return count || false
    },
    showOrderButton: function () {
      return (localStorage.getItem('cart') ? JSON.parse(localStorage.getItem('cart')) : []).length > 0
    },
    generateLink: function () {
      var bed  = JSON.parse(localStorage.getItem('bed'))
      var date = localStorage.getItem('date')
      return '/preorder/' + bed.ward_id + '/' + bed.bed_id + '/' + date
    },
    logout: function () {
      localStorage.clear()
      this.$router.push('/login')
    },

    // ── Category icon — matched by name ───────────────────
    categoryIconSvg: function (name) {
      var a = 'xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"'
      var n = (name || '').toLowerCase()

      // Beverages / drinks
      if (/beverage|drink|tea|coffee|juice|water|smoothie|shake|rooibos|cocoa|milo/.test(n))
        return '<svg ' + a + '><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>'

      // Breakfast / morning
      if (/breakfast|morning|brunch/.test(n))
        return '<svg ' + a + '><path d="M17 18a5 5 0 0 0-10 0"/><line x1="12" y1="2" x2="12" y2="9"/><line x1="4.22" y1="10.22" x2="5.64" y2="11.64"/><line x1="1" y1="18" x2="3" y2="18"/><line x1="21" y1="18" x2="23" y2="18"/><line x1="18.36" y1="11.64" x2="19.78" y2="10.22"/><line x1="23" y1="22" x2="1" y2="22"/><polyline points="8 6 12 2 16 6"/></svg>'

      // Soup / broth / stew
      if (/soup|broth|stew|chowder|bisque/.test(n))
        return '<svg ' + a + '><path d="M2 12h20"/><path d="M4 12c0 4.4 3.6 8 8 8s8-3.6 8-8"/><path d="M9 4c0 1.7-2 3-2 5"/><path d="M14 4c0 1.7-2 3-2 5"/></svg>'

      // Salad / vegetables / greens / sides
      if (/salad|vegetable|veg|green|side|garden|fresh/.test(n))
        return '<svg ' + a + '><path d="M2 22c4-4 8-12 18-14C19 18 12 22 2 22z"/><path d="M2 22 12 12"/></svg>'

      // Dessert / sweet / cake / pudding
      if (/dessert|sweet|cake|pudding|pastry|biscuit|cookie|confection/.test(n))
        return '<svg ' + a + '><path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8"/><path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2 1 2 1"/><line x1="2" y1="21" x2="22" y2="21"/><path d="M7 8v3"/><path d="M12 8v3"/><path d="M17 8v3"/><path d="M7 4h.01"/><path d="M12 4h.01"/><path d="M17 4h.01"/></svg>'

      // Protein / meat / chicken / grill / braai
      if (/protein|meat|chicken|beef|pork|lamb|grill|braai|poultry/.test(n))
        return '<svg ' + a + '><path d="M12 2c0 0-4 4-4 8s4 8 4 8"/><path d="M12 2c0 0 4 4 4 8s-4 8-4 8"/><line x1="12" y1="2" x2="12" y2="22"/><path d="M4.93 4.93c-1.9 3.2-1.9 7 0 10.14"/><path d="M19.07 4.93c1.9 3.2 1.9 7 0 10.14"/></svg>'

      // Fish / seafood
      if (/fish|seafood|prawn|shrimp|calamari|tuna|salmon|hake/.test(n))
        return '<svg ' + a + '><path d="M6.5 12c-.5-2 1-5 5.5-5 5.5 0 9 3.5 9 5s-3.5 5-9 5c-4.5 0-6-3-5.5-5z"/><path d="M2 7l2.5 5L2 17"/><circle cx="17" cy="12" r="1"/></svg>'

      // Starch / carbs / bread / pasta / rice
      if (/starch|carb|bread|pasta|rice|pap|phuthu|noodle|potato/.test(n))
        return '<svg ' + a + '><path d="M3 7c0-2.2 1.8-4 4-4h10c2.2 0 4 1.8 4 4v1c0 1.1-.9 2-2 2v8a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-8c-1.1 0-2-.9-2-2V7z"/></svg>'

      // Fruit / fresh fruit
      if (/fruit/.test(n))
        return '<svg ' + a + '><path d="M12 20.94c1.5 0 2.75 1.06 4 1.06 3 0 6-8 6-12.22A4 4 0 0 0 17.66 6l-.64.14A4.76 4.76 0 0 1 12 6a4.76 4.76 0 0 1-5.02.14L6.34 6A4 4 0 0 0 2 9.78C2 14 5 22 8 22c1.25 0 2.5-1.06 4-1.06z"/><path d="M10 2c0 1.5 1 2.5 2 3"/></svg>'

      // Snack / light
      if (/snack|light|finger|canape|appetiser|appetizer|starter/.test(n))
        return '<svg ' + a + '><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>'

      // Chef special / special / premium
      if (/special|chef|premium|signature|feature/.test(n))
        return '<svg ' + a + '><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>'

      // Dinner / supper / evening
      if (/dinner|supper|evening|lunch|midday|main/.test(n))
        return '<svg ' + a + '><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/></svg>'

      // Default — plate / utensils
      return '<svg ' + a + '><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>'
    },

    // ── Smart food icon (from Products.vue) ───────────────
    itemIconSvg: function (item) {
      var a = 'xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"'
      var lbl = (item.label || '').toLowerCase()
      var cup   = '<svg ' + a + '><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>'
      var egg   = '<svg ' + a + '><path d="M12 22c5.5 0 8-3.6 8-8.5C20 8.2 16.4 2 12 2S4 8.2 4 13.5C4 18.4 6.5 22 12 22z"/></svg>'
      var soup  = '<svg ' + a + '><path d="M2 12h20"/><path d="M4 12c0 4.4 3.6 8 8 8s8-3.6 8-8"/><path d="M9 4c0 1.7-2 3-2 5"/><path d="M14 4c0 1.7-2 3-2 5"/></svg>'
      var bread = '<svg ' + a + '><path d="M3 7c0-2.2 1.8-4 4-4h10c2.2 0 4 1.8 4 4v1c0 1.1-.9 2-2 2v8a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-8c-1.1 0-2-.9-2-2V7z"/></svg>'
      var fish  = '<svg ' + a + '><path d="M6.5 12c-.5-2 1-5 5.5-5 5.5 0 9 3.5 9 5s-3.5 5-9 5c-4.5 0-6-3-5.5-5z"/><path d="M2 7l2.5 5L2 17"/><circle cx="17" cy="12" r="1"/></svg>'
      var leaf  = '<svg ' + a + '><path d="M2 22c4-4 8-12 18-14C19 18 12 22 2 22z"/><path d="M2 22 12 12"/></svg>'
      var fruit = '<svg ' + a + '><path d="M12 20.94c1.5 0 2.75 1.06 4 1.06 3 0 6-8 6-12.22A4 4 0 0 0 17.66 6l-.64.14A4.76 4.76 0 0 1 12 6a4.76 4.76 0 0 1-5.02.14L6.34 6A4 4 0 0 0 2 9.78C2 14 5 22 8 22c1.25 0 2.5-1.06 4-1.06z"/><path d="M10 2c0 1.5 1 2.5 2 3"/></svg>'
      var cake  = '<svg ' + a + '><path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8"/><path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2 1 2 1"/><line x1="2" y1="21" x2="22" y2="21"/><path d="M7 8v3"/><path d="M12 8v3"/><path d="M17 8v3"/><path d="M7 4h.01"/><path d="M12 4h.01"/><path d="M17 4h.01"/></svg>'
      var bowl  = '<svg ' + a + '><path d="M3 11l19-9-9 19-2-8-8-2z"/></svg>'
      var knife = '<svg ' + a + '><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/></svg>'
      if (/tea|coffee|rooibos|cocoa|milo|hot.?drink|beverage|juice|water|smoothie|shake/.test(lbl)) return cup
      if (/\begg\b|omelette|scrambled|boiled|fried.?egg|breakfast|cereal|porridge|oat|muesli|pancake/.test(lbl)) return egg
      if (/soup|broth|chowder|bisque|veloute|consomme|minestrone/.test(lbl)) return soup
      if (/fish|tuna|salmon|hake|sardine|prawn|shrimp|calamari|seafood/.test(lbl)) return fish
      if (/sandwich|bread|roll|toast|wrap|roti|pita|bun|croissant|scone|muffin/.test(lbl)) return bread
      if (/\bfruit\b|apple|orange|banana|mango|grape|melon|berr/.test(lbl)) return fruit
      if (/salad|spinach|broccoli|vegetable|coleslaw|chakalaka/.test(lbl)) return leaf
      if (/dessert|cake|pudding|jelly|custard|tart|biscuit|cookie|sweet|chocolate|ice.?cream/.test(lbl)) return cake
      if (/\brice\b|pap|phuthu|pasta|spaghetti|noodle|potato|chips|maize/.test(lbl)) return bowl
      return knife
    }
  }
}
</script>

<style scoped>
.categories-page {
  min-height: 100vh;
  background: #f0f4f8;
  padding-bottom: 40px;
}

/* ── Page header ── */
.page-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 30px 20px 20px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  position: fixed;
  top: 0; left: 0;
  width: 100%;
  z-index: 10;
  margin-top: 3.2rem;
  box-shadow: 0 8px 32px rgba(15, 61, 92, 0.35);
}
.back-btn {
  width: 36px; height: 36px; border-radius: 10px;
  background: #fb5653; border: 1px solid rgba(255,255,255,0.2);
  color: #fff; display: flex; align-items: center; justify-content: center;
  font-size: 1rem; cursor: pointer; flex-shrink: 0; transition: background 0.15s;
}
.back-btn:hover { background: #e03e3b; }
.header-info { flex: 1; display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.page-title   { font-size: 1.05rem; font-weight: 700; color: #fff; }
.page-subtitle{ font-size: 0.73rem; color: rgba(255,255,255,0.65); font-weight: 500; display: flex; align-items: center; gap: 4px; }
.page-subtitle i { font-size: 0.65rem; }
.cart-btn {
  position: relative; width: 38px; height: 38px; border-radius: 10px;
  background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.2);
  color: #fff !important; display: flex; align-items: center; justify-content: center;
  font-size: 1rem; text-decoration: none !important; flex-shrink: 0;
}
.cart-badge {
  position: absolute; top: -5px; right: -5px;
  width: 18px; height: 18px; border-radius: 50%;
  background: #fb5653; color: #fff; font-size: 0.6rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #1a587b;
}

/* ── Section ── */
.section-pad { padding: 110px 16px 0; }
.section-pad + .section-pad { padding-top: 14px; }
.cat-section  { padding-top: 110px; }
.items-section{ padding-top: 14px; }

/* ── Inline cart confirmation toast ── */
.cart-toast {
  position: fixed;
  top: 120px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 200;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
  background: #0f3d5c;
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(15, 61, 92, 0.35);
  min-width: 260px;
  max-width: 300px;
  pointer-events: auto;
}

.cart-toast-icon {
  width: 30px; height: 30px; border-radius: 50%;
  background: #10b981;
  display: flex; align-items: center; justify-content: center;
  color: #fff; flex-shrink: 0;
}

.cart-toast-body {
  display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0;
}

.cart-toast-title {
  font-size: 0.72rem; font-weight: 700;
  color: rgba(255,255,255,0.65);
  text-transform: uppercase; letter-spacing: 0.6px;
}

.cart-toast-item {
  font-size: 0.9rem; font-weight: 700; color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.cart-toast-close {
  background: rgba(255,255,255,0.12); border: none; border-radius: 8px;
  color: rgba(255,255,255,0.7); width: 26px; height: 26px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; flex-shrink: 0; transition: background 0.15s;
}
.cart-toast-close:hover { background: rgba(255,255,255,0.2); }

/* slide up / fade in */
.toast-slide-enter-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.toast-slide-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.toast-slide-enter        { opacity: 0; transform: translateX(-50%) translateY(-16px); }
.toast-slide-leave-to     { opacity: 0; transform: translateX(-50%) translateY(-16px); }

/* ── Loading / empty ── */
.loading-state {
  display: flex; flex-direction: column; align-items: center;
  gap: 14px; padding: 120px 16px 40px; color: #94a3b8; font-size: 0.875rem;
}
.spinner {
  position: relative; width: 38px; height: 38px; border-radius: 50%;
  border: 3px solid transparent; border-top-color: #1a587b; border-left-color: #1a587b;
  animation: spin 0.9s cubic-bezier(0.45,0.05,0.55,0.95) infinite;
}
.spinner::after {
  content: ''; position: absolute; inset: 5px; border-radius: 50%;
  border: 2.5px solid transparent;
  border-top-color: rgba(26,88,123,0.35); border-left-color: rgba(26,88,123,0.35);
  animation: spin 1.8s cubic-bezier(0.45,0.05,0.55,0.95) infinite reverse;
}
@keyframes spin { to { transform: rotate(360deg); } }

.empty-state {
  display: flex; flex-direction: column; align-items: center;
  gap: 10px; padding: 120px 32px 40px; text-align: center;
}
.empty-icon-wrap {
  width: 72px; height: 72px; border-radius: 20px;
  background: #e0f2fe; color: #1a587b;
  display: flex; align-items: center; justify-content: center; margin-bottom: 4px;
}
.empty-title { font-size: 1rem; font-weight: 700; color: #0f172a; margin: 0; }
.empty-sub   { font-size: 0.85rem; color: #94a3b8; margin: 0; max-width: 280px; line-height: 1.5; }
.empty-back-btn {
  margin-top: 6px; padding: 9px 20px; border-radius: 10px;
  background: #1a587b; color: #fff; border: none;
  font-size: 0.83rem; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; gap: 7px;
}
.empty-items {
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; padding: 32px 16px; color: #94a3b8; text-align: center;
}
.empty-items i  { font-size: 1.8rem; }
.empty-items p  { font-size: 0.875rem; margin: 0; }

/* ── Category scroll strip ── */
.cat-scroll-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.scroll-arrow {
  flex-shrink: 0;
  width: 30px; height: 30px; border-radius: 50%;
  border: 1.5px solid #e2e8f0; background: #fff;
  color: #1a587b; display: flex; align-items: center; justify-content: center;
  cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: background 0.15s, border-color 0.15s, transform 0.1s; z-index: 1;
}
.scroll-arrow:hover  { background: #e0f2fe; border-color: #1a587b; }
.scroll-arrow:active { transform: scale(0.92); }

.cat-strip {
  display: flex;
  flex-direction: row;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 2px 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  flex: 1;
}
.cat-strip::-webkit-scrollbar { display: none; }

.cat-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 18px 22px;
  border-radius: 999px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #334155;
  font-size: 1.25rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.cat-chip:hover  { border-color: #1a587b; color: #1a587b; }
.cat-chip:active { transform: scale(0.96); }

.cat-chip--active {
  background: #1a587b;
  border-color: #1a587b;
  color: #fff;
  box-shadow: 0 3px 12px rgba(26,88,123,0.28);
}

.cat-chip-icon {
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; color: #ef5953;
}
.cat-chip--active .cat-chip-icon { color: #fff; }

.cat-chip-added {
  font-size: 0.65rem; font-weight: 800;
  background: #d1fae5; color: #059669;
  padding: 1px 6px; border-radius: 999px;
}
.cat-chip--active .cat-chip-added {
  background: rgba(255,255,255,0.2); color: #fff;
}

/* ── Product grid ── */
.product-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
@media (min-width: 600px)  { .product-grid { grid-template-columns: repeat(3, 1fr); } }
@media (min-width: 900px)  { .product-grid { grid-template-columns: repeat(4, 1fr); } }

/* ── Product card ── */
.product-card {
  position: relative;
  background: #fff;
  border-radius: 14px;
  padding: 16px 12px 12px;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  cursor: pointer; text-align: center;
  /* border: 2px solid #124564; */
  box-shadow: 0 -3px 8px rgba(18,69,100,0.18), 0 5px 14px rgba(18,69,100,0.22);
  transition: box-shadow 0.2s, transform 0.15s;
  overflow: hidden;
}
.product-card:hover  { box-shadow: 0 -4px 10px rgba(18,69,100,0.22), 0 7px 18px rgba(18,69,100,0.28); transform: translateY(-2px); }
.product-card:active { transform: scale(0.97); }
.product-card--ordered { border: 2px solid #10b981; }

.cart-count {
  position: absolute; top: 8px; right: 8px;
  min-width: 20px; height: 20px; border-radius: 999px;
  background: #fb5653; color: #fff;
  font-size: 0.65rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center; padding: 0 4px;
}

.product-icon-wrap {
  width: 52px; height: 52px; border-radius: 14px;
  background: #f0f8ff;
  display: flex; align-items: center; justify-content: center;
  color: #1a587b; flex-shrink: 0;
}

.product-name {
  font-size: 0.82rem; font-weight: 700; color: #0f172a; line-height: 1.3;
}

.product-desc {
  font-size: 0.72rem; color: #94a3b8; line-height: 1.4; margin: 0;
  display: -webkit-box; -webkit-line-clamp: 2; line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}

.select-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 14px; border-radius: 999px;
  background: #1a587b; color: #fff;
  font-size: 0.75rem; font-weight: 700;
  margin-top: 4px;
  transition: opacity 0.15s;
}
.product-card:hover .select-btn { opacity: 0.88; }
.select-btn--ordered { background: #10b981; }

/* ── Place another order (inline action btn) ── */
.action-btn--order-again {
  display: flex; align-items: center; justify-content: center; gap: 7px;
  flex: 1; padding: 13px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  color: #fff !important; border-radius: 11px;
  font-size: 0.85rem; font-weight: 700;
  text-decoration: none !important;
  transition: opacity 0.2s;
}
.action-btn--order-again:hover { opacity: 0.9; }
</style>
